import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api-client";

type Status = "loading" | "authed" | "anon" | "unconfigured";

type AuthContextValue = {
  status: Status;
  refresh: () => Promise<void>;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    try {
      const who = await api.admin.whoami();
      if (!who.configured) setStatus("unconfigured");
      else setStatus(who.authed ? "authed" : "anon");
    } catch {
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (password: string) => {
      await api.admin.login(password);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.admin.logout();
    await refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ status, refresh, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAdminAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
